// Who may see the credit-purchase surfaces.
//
// This is a UX gate. The authoritative boundary is server-side
// (`require_billing_user` on every /billing route) — hiding a button is not
// security, and this file must never be the only thing standing between a user
// and a billing operation.
//
// It MUST agree with the backend predicate, though, or the UI shows an
// affordance that 403s the moment it is used. The backend allows:
//     is_meta_email(email)
//  OR (settings.debug AND is_local_environment() AND user is the debug user)
// so meta_only mirrors those exactly. billing_audience=all admits signed-in
// users; unknown values retain the meta_only policy.

import { isMetaEmail } from '../../utils/metaEmail'

/** Only the field this gate reads, projected from the real auth user type. */
type BillingPrincipal = { email?: string | null } | null | undefined

/**
 * Local auth-disabled dev. Mirrors `isAdminLocalDev()` in
 * components/admin/adminAccess.ts.
 *
 * `import.meta.env.DEV` is resolved at BUILD time, so this whole expression is
 * statically false in a production bundle — a deployed console cannot enable
 * the bypass by any runtime means (hostname spoofing included).
 *
 * `!VITE_ENABLE_AUTH` is a truthiness test on a string, so the literal
 * `VITE_ENABLE_AUTH='false'` disables the bypass rather than enabling it.
 * That is deliberate here: it fails CLOSED, and it is character-for-character
 * what `isAdminLocalDev()` (components/admin/adminAccess.ts) and
 * `ProtectedRoute`'s `isLocalDev` already do. Making this one stricter-but-
 * different would mean billing and admin disagreed about what "local dev"
 * means in the same session, which is a worse failure than the string quirk.
 * If it is fixed, fix all three together.
 */
export function isLocalDevBypass(): boolean {
  return (
    import.meta.env.DEV &&
    !import.meta.env.VITE_ENABLE_AUTH &&
    typeof window !== 'undefined' &&
    (window.location.hostname === 'localhost' || window.location.hostname === '127.0.0.1')
  )
}

/**
 * Whether to surface billing to this principal.
 *
 * The `user == null` arm is required, not sloppy: with auth disabled locally
 * there is no user object at all, and the backend resolves those requests to
 * the debug user. Without it the section would be hidden in exactly the setup
 * where the API would have allowed it.
 */
export function canSeeBilling(
  user: BillingPrincipal,
  localBypass: boolean = isLocalDevBypass(),
  audience: string = 'meta_only',
): boolean {
  if (audience === 'all') return user != null
  if (isMetaEmail(user?.email)) return true
  return localBypass && (user == null || user.email == null || user.email === 'debug@localhost')
}
